"use client";

import { useDispatchEmail } from "@/hooks/useDispatchEmail";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { EmailEvents } from "@root/types/email";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { BsPerson } from "react-icons/bs";
import { MdOutlineEmail } from "react-icons/md";
import Navbar from "./Navbar";
import Image from "next/image";
import { useViewport } from "@/hooks/useViewport";
import heroBg from "@root/public/images/photos/clarifayo_ninho.png";

export const ContactForm = () => {
  const { isMobile } = useViewport();
  const t = useTranslations();
  const [sending, setSending] = useState(false);
  const toast = useToast();
  const dispatchEmail = useDispatchEmail();

  const { register, handleSubmit, reset } = useForm();

  const onSubmit = async (data: any) => {
    try {
      setSending(true);
      await dispatchEmail({
        event: EmailEvents.Contact,
        ...data,
      });
      toast({
        title: "Success",
        description: "Your message has been sent!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      reset({
        fullName: "",
        email: "",
        message: "",
      });
    } catch (e: any) {
      console.error(e);
      toast({
        title: "Error",
        description: "Something went wrong, please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack
      gap={{ base: 8, md: 14 }}
      pt={{ base: 24, md: 24 }}
      px={{
        base: 8,
        md: 20,
      }}
      position="relative"
      alignItems="center"
      className="landing-page"
    >
      <Box className="hero-bg">
        {isMobile ? (
          <Image
            src={heroBg}
            alt="hero background"
            width={4352 / 4}
            height={2901 / 4}
            quality={80}
            objectFit="cover"
            loading="eager"
            style={{
              position: "absolute",
              objectFit: "cover",
              left: 0,
              top: 0,
              height: "100%",
              zIndex: 0,
            }}
          />
        ) : (
          <Image
            src={heroBg}
            alt="hero background"
            width={4352 / 4}
            height={2901 / 4}
            quality={80}
            objectFit="cover"
            loading="eager"
            style={{
              position: "absolute",
              aspectRatio: "16/9",
              left: 0,
              top: 0,
              maxWidth: "100%",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 0,
            }}
          />
        )}
      </Box>
      <Box position="absolute" top={0} left={0} w="full">
        <Navbar />
      </Box>
      <Flex
        w="full"
        alignItems="center"
        justifyContent="center"
        id="contact"
        mb={20}
      >
        <Box
          borderRadius="lg"
          m={{ base: 0, md: 16, lg: 8 }}
          width={{
            base: "full",
            md: "600px",
          }}
        >
          <Box w="full">
            <Stack
              w="full"
              spacing={{ base: 4, md: 4, lg: 4 }}
              direction={{ base: "column", md: "column" }}
            >
              <Box
                py={{ base: 16, md: 16 }}
                px={{
                  base: 8,
                  md: 16,
                }}
                rounded="28px"
                bgColor="brand.1000"
                position="relative"
                color="white"
              >
                <VStack spacing={5}>
                  <FormControl isRequired>
                    <FormLabel
                      fontSize="sm"
                      textTransform={"uppercase"}
                      color="brand.100"
                    >
                      {t("name")}
                    </FormLabel>

                    <InputGroup>
                      <InputLeftElement>
                        <BsPerson />
                      </InputLeftElement>
                      <Input
                        bgColor="brand.1000"
                        borderColor="brand.1000"
                        borderRadius="0"
                        borderBottomColor="brand.700"
                        type="text"
                        placeholder={t("yourName")}
                        {...register("fullName", { required: true })}
                      />
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel
                      fontSize="sm"
                      textTransform={"uppercase"}
                      color="brand.100"
                    >
                      Email
                    </FormLabel>

                    <InputGroup>
                      <InputLeftElement>
                        <MdOutlineEmail />
                      </InputLeftElement>
                      <Input
                        bgColor="brand.1000"
                        borderColor="brand.1000"
                        borderRadius="0"
                        borderBottomColor="brand.700"
                        type="email"
                        {...register("email", { required: true })}
                        placeholder={t("yourEmail")}
                      />
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel
                      fontSize="sm"
                      textTransform={"uppercase"}
                      color="brand.100"
                    >
                      {t("message")}
                    </FormLabel>

                    <Textarea
                      bgColor="brand.1000"
                      borderColor="brand.1000"
                      borderRadius="0"
                      borderBottomColor="brand.700"
                      {...register("message", { required: true })}
                      placeholder={t("yourMessage")}
                      rows={6}
                      resize="none"
                    />
                  </FormControl>

                  <Button
                    variant="primary"
                    rounded="full"
                    width="full"
                    isLoading={sending}
                    isDisabled={sending}
                    onClick={handleSubmit(onSubmit)}
                  >
                    {t("send")}
                  </Button>
                </VStack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Flex>
    </Stack>
  );
};
